import { type FC } from 'react';

import {
  ButtonField,
  Dropdown,
  DropdownList,
  DropdownListItem,
  DropdownMoreButton,
  SelectField,
} from '@wk/components-react16';
import {
  IconBrain,
  IconBug,
  IconChecklist,
  IconDownload,
  IconFileText,
  IconLoader2,
  IconShare,
} from '@tabler/icons-react';

import { cn } from '@/lib/utils';

import {
  viewLabel,
  type EvaluationView,
} from '../../lib/viewAccess';

interface CheckpointResultActionsProps {
  canSwitchEvaluationView: boolean;
  isMobileViewport: boolean;
  activeView: EvaluationView;
  availableViews: EvaluationView[];
  onViewChange: (view: EvaluationView) => void;
  onOpenCheckpointNavigator: () => void;
  isActionsDropdownOpen: boolean;
  onActionsDropdownChange: (isOpen: boolean) => void;
  onOpenReportBug: () => void;
  onShare: () => void;
  docsUrl: string | null;
  showDocsItem?: boolean;
  onExportCsv: () => void;
  isExportingCsv: boolean;
  exportCsvLabel?: string;
  shouldShowAiAnalysisAction: boolean;
  canRunAiAnalysis: boolean;
  aiLoading: boolean;
  onRunAiAnalysis: () => void;
}

const CheckpointResultActions: FC<CheckpointResultActionsProps> = ({
  canSwitchEvaluationView,
  isMobileViewport,
  activeView,
  availableViews,
  onViewChange,
  onOpenCheckpointNavigator,
  isActionsDropdownOpen,
  onActionsDropdownChange,
  onOpenReportBug,
  onShare,
  docsUrl,
  showDocsItem,
  onExportCsv,
  isExportingCsv,
  exportCsvLabel = 'Download Excel',
  shouldShowAiAnalysisAction,
  canRunAiAnalysis,
  aiLoading,
  onRunAiAnalysis,
}) => {
  const shouldRenderDocsItem = showDocsItem ?? Boolean(docsUrl);
  const actionsTriggerId = isMobileViewport
    ? 'evaluation-mobile-actions-trigger'
    : 'checkpoint-actions';

  if (!canSwitchEvaluationView) {
    return (
      <>
        {isMobileViewport ? (
          <div className="checkpointResult__mobileTopActions">
            <ButtonField mode="ghost" size="small">
              <button
                type="button"
                aria-label="Open checkpoints list"
                onClick={onOpenCheckpointNavigator}
              >
                <IconChecklist size={16} />
              </button>
            </ButtonField>
          </div>
        ) : null}
        <ButtonField size="small" isDanger iconPosition="left">
          <button onClick={onOpenReportBug}>
            <IconBug size={16} />
            Report Bug
          </button>
        </ButtonField>
        <ButtonField size="small" iconPosition="left">
          <button onClick={onShare}>
            <IconShare size={16} />
            Share
          </button>
        </ButtonField>
        {docsUrl ? (
          <ButtonField size="small" iconPosition="left">
            <button onClick={() => window.open(docsUrl, '_blank')}>
              <IconFileText size={16} />
              View docs
            </button>
          </ButtonField>
        ) : null}
        <ButtonField size="small" iconPosition="left">
          <button onClick={onExportCsv} disabled={isExportingCsv}>
            {isExportingCsv ? (
              <IconLoader2 className="animate-spin" size={16} />
            ) : (
              <IconDownload size={16} />
            )}
            {exportCsvLabel}
          </button>
        </ButtonField>
        {shouldShowAiAnalysisAction ? (
          <ButtonField size="small" iconPosition="left">
            <button onClick={onRunAiAnalysis} disabled={aiLoading || !canRunAiAnalysis}>
              {aiLoading ? (
                <IconLoader2 className="animate-spin" size={16} />
              ) : (
                <IconBrain size={16} />
              )}
              Run AI Analysis
            </button>
          </ButtonField>
        ) : null}
      </>
    );
  }

  return (
    <>
      {isMobileViewport ? (
        <div className="checkpointResult__mobileTopActions">
          <ButtonField mode="ghost" size="small">
            <button
              type="button"
              aria-label="Open checkpoints list"
              onClick={onOpenCheckpointNavigator}
            >
              <IconChecklist size={16} />
            </button>
          </ButtonField>
          <div className="checkpointResult__moreActions">
            <DropdownMoreButton
              mode="blue"
              buttonId="evaluation-mobile-actions-trigger"
              onButtonClick={() => onActionsDropdownChange(true)}
            ></DropdownMoreButton>
          </div>
        </div>
      ) : null}

      {!isMobileViewport ? (
        <SelectField>
          <select
            value={activeView}
            onChange={(event) => onViewChange(event.target.value as EvaluationView)}
          >
            {availableViews.map((viewOption) => (
              <option key={viewOption} value={viewOption}>
                {viewLabel(viewOption)}
              </option>
            ))}
          </select>
        </SelectField>
      ) : null}

      {!isMobileViewport ? (
        <div className="checkpointResult__moreActions">
          <DropdownMoreButton
            mode="blue"
            buttonId="checkpoint-actions"
            onButtonClick={() => onActionsDropdownChange(true)}
          ></DropdownMoreButton>
        </div>
      ) : null}

      <Dropdown
        targetSelector={`#${actionsTriggerId}`}
        controlMode="controlled"
        menuMode="application"
        srLabelledby={actionsTriggerId}
        isOpen={isActionsDropdownOpen}
        onUserRequest={(isOpen) => onActionsDropdownChange(isOpen)}
      >
        <DropdownList menuMode="application">
          <DropdownListItem
            onItemClick={() => {
              onActionsDropdownChange(false);
              onOpenReportBug();
            }}
            slot="dropdownListItem"
          >
            <span className="flex items-center gap-2 text-red-500">
              <IconBug size={16} />
              Report Bug
            </span>
          </DropdownListItem>

          <DropdownListItem
            onItemClick={() => {
              onShare();
              onActionsDropdownChange(false);
            }}
            slot="dropdownListItem"
          >
            <span className="flex items-center gap-2">
              <IconShare size={16} />
              Share
            </span>
          </DropdownListItem>

          {shouldRenderDocsItem ? (
            <DropdownListItem
              onItemClick={() => {
                if (!docsUrl) return;
                window.open(docsUrl, '_blank');
                onActionsDropdownChange(false);
              }}
              slot="dropdownListItem"
            >
              <span
                className={cn('flex items-center gap-2', {
                  'cursor-not-allowed opacity-50': !docsUrl,
                })}
              >
                <IconFileText size={16} />
                View docs
              </span>
            </DropdownListItem>
          ) : null}

          <DropdownListItem onItemClick={onExportCsv} slot="dropdownListItem">
            <span className="flex items-center gap-2">
              <IconDownload size={16} />
              {exportCsvLabel}
            </span>
            {isExportingCsv ? (
              <IconLoader2 className="animate-spin" size={16} />
            ) : null}
          </DropdownListItem>

          {shouldShowAiAnalysisAction ? (
            <DropdownListItem
              onItemClick={() => {
                if (!canRunAiAnalysis) return;
                onActionsDropdownChange(false);
                onRunAiAnalysis();
              }}
              slot="dropdownListItem"
            >
              <span
                className={cn('flex items-center gap-2', {
                  'cursor-not-allowed opacity-50': !canRunAiAnalysis,
                })}
              >
                <IconBrain size={16} />
                {aiLoading ? (
                  <IconLoader2 className="animate-spin" size={16} />
                ) : null}
                Run AI Analysis
              </span>
            </DropdownListItem>
          ) : null}
        </DropdownList>
      </Dropdown>
    </>
  );
};

export default CheckpointResultActions;
